/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomImportButton.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";
import { showToastMessage } from "../../redux/actions/actionHandler";
import customConsole from "../../config/customConsole";
import { ReactComponent as UploadStrokeIcon } from "../../assets/logoIcons/upload__l__stroke.svg";

function CustomImportButton({ handleImportedJsonExcelFile }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFileRead = async (file, type) => {
    if (type === "json") {
      try {
        const content = await file.text();
        const jsonData = JSON.parse(content);
        customConsole("Imported JSON Data: ");
        customConsole(jsonData);
        handleImportedJsonExcelFile({
          fileType: "json",
          importedFileData: jsonData,
        });
      } catch (error) {
        console.error("Invalid JSON file format.", error);
        showToastMessage({
          type: "error",
          message: "Invalid JSON file format.",
        });
      }
    } else if (type === "excel") {
      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const allSheetsData = {};
        for (const sheetName of workbook.SheetNames) {
          allSheetsData[sheetName] = XLSX.utils.sheet_to_json(
            workbook.Sheets[sheetName],
          );
        }
        customConsole("Imported Excel Data: ");
        customConsole(allSheetsData);
        handleImportedJsonExcelFile({
          fileType: "excel",
          importedFileData: allSheetsData,
        });
      } catch (error) {
        console.error("Unable to process Excel file.", error);
        showToastMessage({
          type: "error",
          message: "Unable to process Excel file.",
        });
      }
    } else {
      showToastMessage({
        type: "error",
        message: "Unsupported file type.",
      });
    }
  };

  const handleFileSelect = (type) => {
    handleClose();
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = type === "json" ? ".json" : ".xls,.xlsx";

    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        const fileType = file.name.split(".").pop().toLowerCase();

        if (type === "json" && fileType !== "json") {
          showToastMessage({
            type: "error",
            message: "Please select a valid JSON file.",
          });
        } else if (type === "excel" && !["xls", "xlsx"].includes(fileType)) {
          showToastMessage({
            type: "error",
            message: "Please select a valid Excel file.",
          });
        } else {
          handleFileRead(file, type);
        }
      }
    };

    fileInput.click();
  };

  return (
    <Box>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: "black", filter: "drop-shadow(0 0 2px rgba(0,0,0,0.5))" }}
      >
        <UploadStrokeIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleFileSelect("json")}>
          Import JSON
        </MenuItem>
        <MenuItem onClick={() => handleFileSelect("excel")}>
          Import Excel
        </MenuItem>
      </Menu>
    </Box>
  );
}

CustomImportButton.propTypes = {
  handleImportedJsonExcelFile: PropTypes.func,
};

export default CustomImportButton;
